import React, { useState } from 'react';
import Card from './components/Card';
import cardsData from './data/cards.json';
import './styles/App.css';

const SPREADS = {
  'single': {
    name: 'Single Card',
    positions: [{ name: 'Insight', class: 'single-card' }]
  },
  'three': {
    name: 'Three Card Spread',
    positions: [
      { name: 'Card 1', class: 'position-1' },
      { name: 'Card 2', class: 'position-2' },
      { name: 'Card 3', class: 'position-3' }
    ]
  },
  'five': {
    name: 'Five Card Spread',
    positions: [
      { name: 'Card 1', class: 'position-1' },
      { name: 'Card 2', class: 'position-2' },
      { name: 'Card 3', class: 'position-3' },
      { name: 'Card 4', class: 'position-4' },
      { name: 'Card 5', class: 'position-5' }
    ]
  },
  'seven-s': {
    name: 'McKinsey 7S',
    positions: [
      { name: 'Shared Values', class: 'sevens-center' },
      { name: 'Strategy', class: 'sevens-1' },
      { name: 'Structure', class: 'sevens-2' },
      { name: 'Systems', class: 'sevens-3' },
      { name: 'Style', class: 'sevens-4' },
      { name: 'Staff', class: 'sevens-5' },
      { name: 'Skills', class: 'sevens-6' }
    ]
  },
  'balanced-scorecard': {
    name: 'Balanced Scorecard',
    positions: [
      { name: 'Financial', class: 'bsc-tl' },
      { name: 'Customer', class: 'bsc-tr' },
      { name: 'Internal Process', class: 'bsc-bl' },
      { name: 'Learning & Growth', class: 'bsc-br' }
    ]
  },
  'bcg-matrix': {
    name: 'Boston Matrix (BCG)',
    positions: [
      { name: 'Stars', class: 'bcg-tl' },
      { name: 'Question Marks', class: 'bcg-tr' },
      { name: 'Cash Cows', class: 'bcg-bl' },
      { name: 'Dogs', class: 'bcg-br' }
    ]
  },
  'swot': {
    name: 'SWOT Analysis',
    positions: [
      { name: 'Strengths', class: 'swot-tl' },
      { name: 'Weaknesses', class: 'swot-tr' },
      { name: 'Opportunities', class: 'swot-bl' },
      { name: 'Threats', class: 'swot-br' }
    ]
  }
};

function App() {
  const [selectedSpread, setSelectedSpread] = useState('three');
  const [dealtCards, setDealtCards] = useState([]);
  const [revealedCards, setRevealedCards] = useState(new Set());
  const [isDealing, setIsDealing] = useState(false);

  // Card category filters - default all checked
  const [includeCharacters, setIncludeCharacters] = useState(true);
  const [includeForces, setIncludeForces] = useState(true);
  const [includeMultipliers, setIncludeMultipliers] = useState(true);

  // Categorize a card based on its filename
  const categorizeCard = (card) => {
    const filename = card.image.toLowerCase();

    if (filename.includes('character')) return 'character';
    if (filename.includes('business') || filename.includes('elements') || filename.includes('vuca')) return 'forces';
    if (filename.includes('money') || filename.includes('products') || filename.includes('customers') || filename.includes('employees')) return 'multipliers';

    return 'other';
  };

  // Filter cards based on selected categories
  const getFilteredCards = () => {
    return cardsData.filter(card => {
      const category = categorizeCard(card);

      if (category === 'character' && includeCharacters) return true;
      if (category === 'forces' && includeForces) return true;
      if (category === 'multipliers' && includeMultipliers) return true;

      return false;
    });
  };

  const shuffleAndDeal = () => {
    setIsDealing(true);

    // If cards are already dealt, flip them back first
    if (dealtCards.length > 0) {
      setRevealedCards(new Set());

      // Wait for flip animation to complete (600ms) before dealing new cards
      setTimeout(() => {
        dealNewCards();
      }, 600);
    } else {
      // No cards dealt yet, deal immediately
      dealNewCards();
    }
  };

  const dealNewCards = () => {
    // Get filtered cards based on category selection
    const filteredCards = getFilteredCards();

    // If no cards available, don't deal
    if (filteredCards.length === 0) {
      setIsDealing(false);
      return;
    }

    // Shuffle filtered cards
    const shuffled = [...filteredCards].sort(() => Math.random() - 0.5);
    const numCards = SPREADS[selectedSpread].positions.length;
    const dealt = shuffled.slice(0, Math.min(numCards, filteredCards.length));

    setDealtCards(dealt);
    setRevealedCards(new Set());
    setIsDealing(false);
  };

  const revealCard = (index) => {
    setRevealedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const revealAll = () => {
    const allIndices = Array.from({ length: dealtCards.length }, (_, i) => i);
    setRevealedCards(new Set(allIndices));
  };

  const resetSpread = () => {
    setDealtCards([]);
    setRevealedCards(new Set());
  };

  return (
    <div className="App">
      <header>
        <h1>Business Meerkat Cards</h1>
        <p>Explore business insights through the wisdom of the cards</p>
      </header>

      <div className="controls">
        <div className="spread-selector">
          <label htmlFor="spread-select">Choose a spread:</label>
          <select
            id="spread-select"
            value={selectedSpread}
            onChange={(e) => {
              setSelectedSpread(e.target.value);
              resetSpread();
            }}
            disabled={isDealing}
          >
            {Object.entries(SPREADS).map(([key, spread]) => (
              <option key={key} value={key}>
                {spread.name}
              </option>
            ))}
          </select>
        </div>

        <div className="card-filters">
          <label>Include cards:</label>
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={includeCharacters}
                onChange={(e) => setIncludeCharacters(e.target.checked)}
              />
              <span>Characters</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={includeForces}
                onChange={(e) => setIncludeForces(e.target.checked)}
              />
              <span>Forces</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={includeMultipliers}
                onChange={(e) => setIncludeMultipliers(e.target.checked)}
              />
              <span>Multipliers</span>
            </label>
          </div>
        </div>

        <div className="action-buttons">
          <button onClick={shuffleAndDeal} disabled={isDealing}>
            {dealtCards.length > 0 ? 'New Reading' : 'Deal Cards'}
          </button>
          {dealtCards.length > 0 && (
            <>
              <button onClick={revealAll} disabled={revealedCards.size === dealtCards.length}>
                Reveal All
              </button>
              <button onClick={resetSpread}>Reset</button>
            </>
          )}
        </div>
      </div>

      {dealtCards.length > 0 && (
        <div className={`spread-container spread-${selectedSpread}`}>
          {dealtCards.map((card, index) => (
            <div key={index} className="card-position">
              <div className="position-label">
                {SPREADS[selectedSpread].positions[index].name}
              </div>
              <Card
                card={card}
                isRevealed={revealedCards.has(index)}
                onReveal={() => revealCard(index)}
                position={SPREADS[selectedSpread].positions[index].class}
              />
            </div>
          ))}
        </div>
      )}

      {dealtCards.length === 0 && (
        <div className="empty-state">
          <p>Select a spread and click "Deal Cards" to begin your reading</p>
        </div>
      )}
    </div>
  );
}

export default App;
